import { useEffect, useMemo, useState } from "react";
import { getPlatforms, IonLabel, IonLoading, IonToggle, useIonToast } from "@ionic/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdditionalEditField } from "components/EditRequest/AdditionalEditsField";
import {
  BudgetField,
  ChooseSubField,
  DayOfContactField,
  filterBudgets,
  GradeField,
  InternalCodeField,
  isRovingTeacher,
  isSubSelectable,
  LanguageField,
  NotesToSubField,
  PositionField,
  SchoolField,
  SubjectField,
  TeacherField,
  TimeToAccept,
  useBudgets,
  useSubs,
} from "components/FormFields";
import { useSchool } from "components/FormFields/SchoolField/hooks";
import { PhoneInput } from "components/PhoneInput/PhoneInput";
import { DateTime, Settings } from "luxon";
import { PositionModel, RequestModel, SchoolModel, SubModel, TeacherKind } from "model";
import { useAuth } from "pages";
// FIXME: Replace with `Icon` from "swing-components"
import { IoHelpCircle } from "react-icons/io5";
import { useHistory } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Colors,
  formatDate,
  Icon,
  IconName,
  Tooltip,
  useFeatureFlags,
  useModal,
} from "swing-components";
import { removeEmptyValues } from "swing-utils";

import { RequestFormDateField } from "~components/FormFields";
import { MAX_INTERVALS } from "~components/FormFields/RequestFormDateField/DayField";
import { WaitlistAutoFillField } from "~components/FormFields/WaitlistAutoFillField";
import {
  ALERT_COLOR_KEY,
  ALERT_DISCLAIMER_KEY,
  ALERT_MESSAGE_KEY,
  ALERT_TITLE_KEY,
  AlertState,
} from "~components/RequestInfoCard/constants";
import {
  api,
  COMBINED_RESTRICTIONS,
  translate,
  useShouldRestrictAdmin,
  useURLSearchParams,
} from "~utils";

import { LatestAcceptableProps, Loader, ReviewRequestSubmission, SharedModalHeader } from "..";
import {
  generateSelectableDaysFromExistingSelectableDays,
  getInitialIntervals,
  getNextValidDate,
  isDateWithin,
  isSameDate,
  TimesTableRowType,
} from "./hooks";
import { fixStartAndEndTimes, shouldFixNow } from "./hooks/useRequestDate.utils";
import styles from "./RequestForm.module.css";
import {
  calculateCredentialRequired,
  diffKeys,
  filterClassroomUsersFromAdmins,
  SubScheduleDay,
} from "./utils";
import { getTimeToAcceptChoices } from "./utils/timeToAccept";

/** Types */
type RequestFormProps = {
  request?: RequestModel;
  onModalSuccess?: () => void;
  onModalDismiss?: () => void;
  schools: Array<SchoolModel>;
  mode?: "edit" | "create";
};

/** Component **/
export default function RequestForm(props: RequestFormProps) {
  /*** Props ****/
  const { request, onModalSuccess, onModalDismiss, schools, mode } = props;

  /*** Constants ****/
  const isEditing = mode === "edit";
  const searchParams = useURLSearchParams();
  const clonedRequestId = searchParams.get("clonedFrom");
  // TODO: We should move this to the RequestDetailsView component since it's
  // not rendered in the form itself.
  const alertState: AlertState = {
    [ALERT_TITLE_KEY]: translate("REQUEST_ALERT_CREATED_HEADER"),
    [ALERT_MESSAGE_KEY]: translate("REQUEST_ALERT_CREATED_MESSAGE"),
  };

  /** Hooks **/
  const { userInfo } = useAuth();
  const [present] = useIonToast();
  const history = useHistory();
  const queryClient = useQueryClient();

  /*** Mutations ***/
  const { mutate: createRequest, isPending: isSubmitPending } = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await api({
        method: "POST",
        url: "/api/admin/request",
        data: payload,
      });
      return data;
    },
    onSuccess: ({ data }) => {
      onModalSuccess?.();
      const isPendingLTRequest = new RequestModel(data).isLTRequestPending();

      const pendingLTRequestAlert = {
        [ALERT_TITLE_KEY]: translate("LABEL_PENDING_SWING_REVIEW_TITLE"),
        [ALERT_MESSAGE_KEY]: translate("LABEL_PENDING_SWING_REVIEW_MESSAGE"),
        [ALERT_COLOR_KEY]: "warning",
      } satisfies AlertState;

      history.push({
        pathname: `/active-requests/${data?.["sub-req/id"]}`,
        state: isPendingLTRequest ? pendingLTRequestAlert : alertState,
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      const traceId = err?.response?.data["trace-id"];
      // eslint-disable-next-line no-console
      console.error(traceId);
      const { message } = err;
      const presentedMessage = `${message}, trace-id: ${traceId}`;
      present({ message: presentedMessage, position: "top", duration: 3000 });
    },
  });

  const { mutate: editRequest, isPending: isEditPending } = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      // Tells TS that `request` is always available in this case.
      if (!request) return;

      const { data } = await api({
        method: "POST",
        url: `/api/admin/request/${request.id}/edit`,
        data: payload,
      });
      return data;
    },
    onSuccess: ({ data }: { data: { "sub-req/id": string } }) => {
      // FIXME: This may not be working as we expect since the documentation
      // shows that it will invalidate based on the `queryKey` which is not
      // passed in. See https://tanstack.com/query/v4/docs/react/reference/QueryClient#queryclientinvalidatequeries
      queryClient.invalidateQueries();

      const filteredIntervals = initialIntervals.filter((interval) => interval.isEnabled);
      const difference = diffKeys(
        request?.getMultiDayRequestFields(filteredIntervals) ?? {},
        multiDayFormState,
      );
      const showUnderReviewAlert = hasAdditionalEdits || difference.includes("multiDayAdjustments");

      let title = translate("LABEL_EDIT_REQUEST_SUCCESS");

      // https://linear.app/swing-education/issue/MKTP-1662/bug-fix-success-message-for-edits-and-review-status-display
      // Show that request is under review IF and only IF, we are editing the additional edits field alone
      if (difference?.length === 1 && difference.includes("additionalEditMemo")) {
        title = translate("LABEL_EDITS_UNDER_REVIEW");
      }

      const historyObj = {
        pathname: `/active-requests/${data?.["sub-req/id"]}`,
        state: {
          [ALERT_TITLE_KEY]: title,
          // Instead, we will send it as a message
          [ALERT_MESSAGE_KEY]: difference,
          [ALERT_DISCLAIMER_KEY]: showUnderReviewAlert && translate("LABEL_ADDITIONAL_EDITS_ALERT"),
        },
      };
      // if on the same page and in a modal, we want to replace the history object
      if (onModalSuccess) {
        onModalSuccess();
        history.replace(historyObj);
      } else {
        // if on a different page, we want to push the history object
        history.push(historyObj);
      }
    },
    onError: () => {
      history.push(`/active-requests/${request?.id}`);
    },
  });

  /*** Hooks ***/
  const isClassroomUser = useShouldRestrictAdmin(COMBINED_RESTRICTIONS.CLASSROOM_ADMIN);

  const { flags } = useFeatureFlags();
  const isLanguageRequirementEnabled = Boolean(flags?.languageRequirementEnabled);
  const isLMRTrimEnabled = Boolean(flags?.lmrTrimEnabled);
  const isPositionAdjustmentEnabled = Boolean(flags?.positionAdjustment);

  // TODO: Improve type safety for formSubmissionErrObj & requiredFieldTouchedObj when a refactor of this form has commenced
  // These objects currently have many keys, and their values can be of various types.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formSubmissionErrObj, setFormSubmissionErrObj] = useState<Record<string, any>>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [requiredFieldTouchedObj, setRequiredFieldTouchedObj] = useState<Record<string, any>>({});

  // Initialize the the Request's School or the first School in the list
  // FIXME: There is a situation where the schools array is empty.
  // TODO: Fix in https://linear.app/swing-education/issue/MKTP-885
  const [schoolId, setSchoolId] = useState(request?.school?.id ?? schools[0].id);
  // FIXME: We are using `!` here but there could be a situation where the
  // School does not match.
  // TODO: Fix in https://linear.app/swing-education/issue/MKTP-885
  const school = schools.find((school) => school.id === schoolId)!;

  // Fetch school data with admin information
  const schoolWithAdmins = useSchool(schoolId);
  const schoolAdmins = schoolWithAdmins?.admins || [];
  const teachers = schoolWithAdmins?.teachers || [];

  // Filter out classroom users (teachers) from admins list for day of contact dropdown
  const admins = filterClassroomUsersFromAdmins(schoolAdmins, teachers);

  const requestorAdmin = { id: userInfo.id || "", name: userInfo.name || "" };

  /*** Luxon Settings ***/
  if (school?.tzString) {
    Settings.defaultZone = school?.tzString;
  }
  // This MUST be below the `Setting.defaultZone` setting for Luxon so that all
  // generated times are in the correct timezone.
  const NOW = DateTime.now().set({ second: 0, millisecond: 0 });

  const [intervals, setIntervals] = useState<Array<TimesTableRowType>>(
    getInitialIntervals({
      now: NOW,
      schoolHours: school.hours,
      schoolClosures: school.closures,
      intervals: request?.getIntervals(true),
    }),
  );

  const hasSubInPool = request?.pool && request?.pool?.length > 0;
  const isFilledOrWaitlisted = request?.isFilled() || hasSubInPool;

  const shouldUsePreviousWeekdayTimes = Boolean(isFilledOrWaitlisted);

  const { data: subSchedule } = useQuery({
    queryKey: ["sub", request?.sub.id, "schedule"],
    queryFn: async () => {
      const newURLParams = new URLSearchParams();

      // set the start and end date url params
      newURLParams.set("start", request?.endDate.toISO() ?? "");
      newURLParams.set("end", request?.endDate.plus({ year: 1 }).toISO() ?? "");

      const { data } = await api({
        method: "GET",
        url: `/api/admin/subs/${request?.sub.id}/schedule?${newURLParams.toString()}`,
      });
      return data;
    },
    select: (data: { data: Array<SubScheduleDay<string>> }) => {
      return data.data.map((date) => ({
        ...date,
        start: DateTime.fromISO(date.start),
        end: DateTime.fromISO(date.end),
      }));
    },
    enabled: Boolean(request?.sub.id),
  });

  // See: https://linear.app/swing-education/issue/MKTP-1070/[swing-schools]-[bug]-disable-submit-edit-button-when-no-edits-are
  // We only want to cache the intervals once on initial render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialIntervals = useMemo(() => intervals, []);

  const [multiDayAdjustmentNotes, setMultiDayAdjustmentNotes] = useState(
    request?.getIntervals() && request?.getIntervals().length > 15
      ? // NOTE: In situations where this field is mistakenly missing, we will
        // gracefully default it to an empty string which will trigger a form
        // invalidation if this is a long term request
        (request?.multiDayAdjustments ?? "")
      : "",
  );
  const [teacher, setTeacher] = useState(request?.teacher || "");
  const [grade, setGrade] = useState<string[]>(
    request?.gradesArray || school?.defaultGrades || userInfo.grades,
  );
  const [subject, setSubject] = useState<string[]>(request?.subjectsArray || userInfo.subjects);
  const [bilingualRequirement, setBilingualRequirement] = useState<string[]>(
    request?.bilingualRequirementArray || [],
  );
  // https://gitlab.com/swing-education/neo/-/merge_requests/1669
  // Used if specific teacher is chosen
  const [teacherId, setTeacherId] = useState(request?.teacherId || "");
  const [teacherKind, setTeacherKind] = useState<TeacherKind | "">(request?.teacher || "");
  const [contactPhone, setContactPhone] = useState<string>(
    request?.contactPhone || userInfo.phone || "",
  );
  const [dayOfContactAdminId, setDayOfContactAdminId] = useState<string>(
    request?.dayOfContactAdminId || (isClassroomUser ? "" : requestorAdmin.id),
  );
  const [internalCode, setInternalCode] = useState(request?.internalCode || "");
  const [isCredentialRequired, setIsCredentialRequired] = useState(
    request ? request?.isCredentialRequired : true,
  );
  const [isLessonPlanProvided, setLessonPlan] = useState(request?.isLessonPlanProvided || false);
  const [memo, setMemo] = useState<string>(request?.notes || school?.defaultNotes || "");
  const [priorityAccessSubs, setPriorityAccessSubs] = useState<Array<SubModel>>([]);

  const [timeToAccept, setTimeToAccept] = useState<number | string>();
  const [latestAcceptableStart, setLatestAcceptableStart] = useState<LatestAcceptableProps>({
    shouldTrim: undefined,
    trimTill: DateTime.now(),
  });
  // Calculate available time-to-accept options based on first enabled interval
  const timeToAcceptChoices = useMemo(() => {
    const firstEnabledInterval = intervals.filter((interval) => interval.isEnabled !== false).at(0);

    if (!firstEnabledInterval) return [];

    return getTimeToAcceptChoices({
      startDateTime: firstEnabledInterval.startTime,
      now: NOW,
    });
  }, [intervals, NOW]);

  const [position, setPosition] = useState<PositionModel | undefined>();

  useEffect(() => {
    // TODO: Clean up this whole block once positionAdjustment flag is removed
    const priorPosition = school?.getPosition(request?.position.id);

    setPosition(
      isPositionAdjustmentEnabled
        ? priorPosition
        : (priorPosition ?? SchoolModel.getDefaultPosition(school?.positions)),
    );
  }, [isPositionAdjustmentEnabled, request, school]);

  const [waitlistAutoFill, setWaitlistAutoFill] = useState<boolean>(
    request?.waitlistAutoFill || false,
  );

  /** Budgets **/
  const { data: allBudgets } = useBudgets({
    showSuperadminBudgets: userInfo?.isSuperAdmin,
    includeSpend: true,
  });
  const [budgetId, setBudgetId] = useState<string | undefined>(request?.budget?.id);
  const budgets = filterBudgets(allBudgets, school!.id, school!.districtId);
  const hasBudgetsToShow = Boolean(budgets.length);

  // Auto-select the first budget if there's only one budget and no
  // budget is selected
  useEffect(() => {
    if (budgets.length === 1 && !budgetId) {
      setBudgetId(budgets[0].id);
    }
  }, [budgets, budgetId]);

  // These values are not currently stored in Datomic so they won't have a value from the model
  const [hasAdditionalEdits, setHasAdditionalEdits] = useState(false);
  const [additionalEditMemo, setAdditionalEditMemo] = useState("");

  const showAdditionalEditField =
    request?.isFilled() || (request?.poolCount && request.poolCount > 0);

  const { openModal, closeModal } = useModal({
    component: (
      <ReviewRequestSubmission
        onDismiss={() => {
          closeModal();
          setLatestAcceptableStart({
            shouldTrim: undefined,
            trimTill: latestAcceptableStart.trimTill,
          });
        }}
        onChange={setLatestAcceptableStart}
        latestAcceptableValues={latestAcceptableStart}
        onSubmit={() => {
          closeModal();
          onSubmit(intervals);
        }}
        school={school}
        intervals={intervals}
      />
    ),
    modalOptions: {
      backdropDismiss: false,
    },
  });

  function isFormValid() {
    const hasError = (v: unknown) => Boolean(v);

    if (memo.trim().length === 0) return false;
    if (hasBudgetsToShow && budgetId === undefined) return false;
    if (Object.values(formSubmissionErrObj).some(hasError)) return false;
    if (school.isInternalCodeRequired && internalCode.trim() === "") return false;

    // Must have a valid admin selected for day of contact if school has admins
    if (schoolAdmins.length > 0 && dayOfContactAdminId.trim() === "") return false;

    // When creating a request, if priority access subs are selected, timeToAccept is required
    if (mode === "create" && priorityAccessSubs.length > 0 && !timeToAccept) return false;

    if (bilingualRequirement.includes("Other")) return false;
    if (
      isPositionAdjustmentEnabled &&
      school?.positions &&
      school?.positions?.length > 0 &&
      !position
    )
      return false;

    return true;
  }

  const multiDayFormState = {
    school: schoolId,
    teacher,
    grade,
    subject,
    bilingualRequirement,
    contactPhone,
    dayOfContactAdminId,
    internalCode,
    isCredentialRequired,
    isLessonPlanProvided,
    memo,
    budgetId,
    position: position?.id,
    additionalEditMemo,
    intervals,
    multiDayAdjustments: multiDayAdjustmentNotes,
  };

  const customizedAdditionalEditMemo = isFilledOrWaitlisted
    ? getExtendedRequestEdits({
        additionalEditMemo,
        intervals,
        initialIntervals,
        subSchedule,
      })
    : undefined;

  const filledRequestFormState = {
    contactPhone,
    dayOfContactAdminId,
    internalCode,
    memo,
    additionalEditMemo,
    intervals,
    budgetId,
    school: schoolId,
    isLessonPlanProvided,
    teacher,
    grade,
    subject,
    bilingualRequirement,
    waitlistAutoFill,
  };

  function isFormChanged() {
    function didFieldsChange(
      beforeEdit: Record<string, unknown>,
      afterEdit: Record<string, unknown>,
    ) {
      // Trim string fields to prevent false diffs from extra spaces
      function normalizeStringFields(obj: Record<string, unknown>) {
        return {
          ...obj,
          memo: typeof obj.memo === "string" ? obj.memo.trim() : "",
          internalCode: typeof obj.internalCode === "string" ? obj.internalCode.trim() : "",
        };
      }
      const a = removeEmptyValues(normalizeStringFields(beforeEdit));
      const b = removeEmptyValues(normalizeStringFields(afterEdit));

      return diffKeys(a, b).length > 0;
    }

    if (!isEditing) {
      return false;
    }

    if (request?.isFilled()) {
      return didFieldsChange(
        request?.getFilledRequestFields(initialIntervals),
        filledRequestFormState,
      );
    }

    // Handle comparing multi day form state
    else {
      return didFieldsChange(
        request?.getMultiDayRequestFields(initialIntervals) ?? {},
        multiDayFormState,
      );
    }
  }

  const enabledIntervals = intervals.filter((interval) => interval.isEnabled);
  const shouldShowReviewModal = isLMRTrimEnabled && enabledIntervals.length === 1;

  function onCreateFormProgress(intervals: TimesTableRowType[]) {
    if (shouldShowReviewModal) {
      openModal();
    } else {
      onSubmit(intervals);
    }
  }

  function onSubmit(_intervals: TimesTableRowType[]) {
    if (!school) return;

    // TODO: Determine why are getting rid of the `!isEnabled` times for the
    // UI here. We should want to keep the UI the same as the user has and
    // only remove the `!isEnabled` times when we are submitting the request.
    setIntervals(_intervals.filter((time) => time.isEnabled));

    // Handle multi day adjustment notes
    let submissibleMDAdjustments: string | null | undefined;
    // If there are more than 15 days between the start and end date
    if (intervals.length > 15) {
      // If the new Long Term Request UI is enabled
      // Then handle an empty string, `""`, as undefined
      submissibleMDAdjustments =
        multiDayAdjustmentNotes.length > 0 ? multiDayAdjustmentNotes : undefined;
    }
    // Otherwise, if the Request is not a Long Term Request
    else {
      // Then ignore the multi day adjustment notes and replace it with null
      submissibleMDAdjustments = null;
    }

    const submissibleIntervals = _intervals
      .filter((item) => item.isEnabled)
      // filter out any intervals that conflict with sub schedule
      .filter(
        (item) =>
          !subSchedule?.some((conflictDay) => isSameDate(conflictDay.start, item.startTime)),
      )
      .map(({ startTime, endTime }) => {
        startTime.setZone(school?.tzString);
        return {
          // TODO: Document why we are not using the `dateString` here.
          start: startTime.toString(),
          end: endTime.toString(),
          offset: Math.abs(startTime.setZone(school?.tzString).offset),
        };
      });

    const credentialRequiredCalculated = calculateCredentialRequired(school, isCredentialRequired);

    const submissibleLatestStart =
      isLMRTrimEnabled && latestAcceptableStart?.shouldTrim
        ? latestAcceptableStart?.trimTill.toString()
        : undefined;

    const requestPayload = {
      ...removeEmptyValues({
        admin: userInfo.id,
        intervals: submissibleIntervals,
        school: schoolId,
        "credential-required?": credentialRequiredCalculated,
        "lesson-plan?": isLessonPlanProvided,
        memo,
        "multi-day-adjustments": submissibleMDAdjustments,
        ...buildPriorityAccessSubsPayload({ priorityAccessSubs, timeToAccept }),
        timestamp: DateTime.now().toString(),
        position: position?.id || undefined,
        // https://linear.app/swing-education/project/swing-schools-extend-filled-waitlisted-requests-24fa9b608736#comment-22d88119
        // If the admin is extending a filled/waitlisted request, then we want to send the extra edits
        "extra-edits": customizedAdditionalEditMemo ?? additionalEditMemo,
        "budget-id": budgetId,
        tags: getPlatforms(),
        "latest-acceptable-start": submissibleLatestStart,
      }),
      // Purposely not removing empty values since an empty string represents
      // that the field should be removed.
      teacher: teacherId || teacher,
      "internal-code": internalCode,
      "contact-number": contactPhone,
      "day-of-contact-admin-id": dayOfContactAdminId,
      "frontline-absence-id": request?.frontlineAbsenceID,
      grade: grade.join(", "),
      subject: subject.join(", "),
      ...(isLanguageRequirementEnabled && {
        "bilingual-requirement": bilingualRequirement.join(", "),
      }),
      "waitlist-auto-fill?": waitlistAutoFill,
      ...(mode === "create" && clonedRequestId && { clonedFrom: clonedRequestId }),
    };

    if (isEditing) {
      editRequest(requestPayload);
    } else {
      createRequest(requestPayload);
    }
  }

  useEffect(() => {
    if (requiredFieldTouchedObj.memo) {
      setFormSubmissionErrObj((error: Record<string, unknown>) => ({
        ...error,
        memo: memo.trim() === "" ? translate("NOTES_TO_SUB_REQ_MEMO_ERROR") : undefined,
      }));
    }
  }, [memo, requiredFieldTouchedObj.memo]);

  const subSelectRequest = useMemo(() => {
    // Given that are are storing times in two formats, one for Single Day and
    // another for Multi Day requests, we must use this ternary to return the
    // correct start and end datetime.
    const start = intervals.filter((interval) => interval.isEnabled)?.at(0)?.startTime;
    const end = intervals.filter((interval) => interval.isEnabled)?.at(-1)?.endTime;

    const isValid = !!(start && end && end >= start);

    return {
      school: schoolId,
      // NOTE: Including `?? 0` to keep TS happy since this field is required
      // but the `school` variable could be `undefined`.
      // FIXME: Why are we using `.abs` here, it's breaking the offset since
      // you can have negative offsets as well as positive offsets from UTC.
      offset: Math.abs(school?.timeZone ?? 0),
      // Use non-null assertion since we guard API calls with isValid
      start: start?.toISO() ?? "",
      end: end?.toISO() ?? "",
      isValid,
      intervals: intervals
        .filter((interval) => interval.isEnabled)
        .map(({ startTime, endTime }) => ({ start: startTime.toISO(), end: endTime.toISO() })),
    };
  }, [schoolId, school, intervals]);

  const { subs: availableSubs } = useSubs({
    query: "",
    request: subSelectRequest,
    // Only fetch when intervals are valid (end >= start)
    enabled: !!request && subSelectRequest.isValid,
  });

  /**
   * Duplicate/Clone Workflow: Pre-populate priority access subs from the
   * original request.
   *
   * Runs once when availableSubs loads. Early returns if:
   * - No request (not a duplicate/clone scenario)
   * - No request.preferredSubs (original request had no priority access)
   * - availableSubs hasn't loaded yet
   *
   * If this is a duplicate with priority access subs, pre-populates the form
   * with those subs (only if the school hasn't changed).
   */
  useEffect(() => {
    if (!request?.preferredSubs || request.preferredSubs.length === 0 || !availableSubs) {
      return;
    }

    const subsFromOriginalRequest = availableSubs.filter(
      (sub: SubModel) =>
        // Must be from the original request
        request.preferredSubs?.includes(sub.id) &&
        // Apply the same selectable logic as ChooseSubField
        isSubSelectable({ sub, isCredentialRequired }),
    );

    if (subsFromOriginalRequest.length > 0 && request?.school.id === schoolId) {
      setPriorityAccessSubs(subsFromOriginalRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSubs]);

  /*** Handlers ***/
  /**
   * When changing schools, keep the start and end date, but reset the selected
   * days so that school closures can be handled correctly.
   */
  function handleSchoolChange(params: { newSchoolId: string }) {
    const { newSchoolId } = params;

    const currentSelectedSchool = schools.find((school) => school.id === newSchoolId)!;

    if (currentSelectedSchool?.defaultNotes) {
      setMemo(request?.notes || currentSelectedSchool.defaultNotes || "");
    }
    setSchoolId(newSchoolId);
    setBudgetId(undefined);
    setTeacher("");
    setPriorityAccessSubs([]); // Reset priority access subs when school changes
    setTeacherId("");
    setTeacherKind("");
    setInternalCode("");
    setFormSubmissionErrObj({});

    // Update grades when school changes (only for new requests, not edits)
    if (!request) {
      setGrade(currentSelectedSchool?.defaultGrades ?? []);
    }

    // Regenerate the selected days given the new school
    const newSchool = schools.find((school) => school.id === newSchoolId)!;
    setIntervals(() =>
      generateSelectableDaysFromExistingSelectableDays({
        now: NOW,
        startDate: getIntervalStartDate(intervals),
        endDate: getIntervalEndDate(intervals),
        selectableDays: [],
        schoolHours: newSchool.hours,
        schoolClosures: newSchool.closures,
        shouldUsePreviousWeekdayTimes,
      }),
    );
  }

  function handleTeacherChange(teacherSelection: string) {
    // Don't apply any changes if the value hasn't changed
    if (teacherSelection === teacher) return;

    const rovingMessage = translate("NOTES_ROVING_MESSAGE");
    const touchMemo = () => {
      setRequiredFieldTouchedObj((touched: Record<string, unknown>) => ({
        ...touched,
        memo: true,
      }));
    };

    setTeacher(teacherSelection);

    // Check if a specific teacher was selected and if they have grades
    // Teacher grades override school default grades; otherwise fall back to school defaults
    const selectedTeacher = school?.teachers?.find(
      (t: { name: string; grades?: string[] }) => t.name === teacherSelection,
    );
    if (selectedTeacher?.grades?.length > 0) {
      setGrade(selectedTeacher.grades);
    } else {
      setGrade(school?.defaultGrades ?? []);
    }

    if (!isRovingTeacher(teacherSelection as TeacherKind)) {
      setMemo((prevMemo) => `${prevMemo.replace(rovingMessage, "").trim()}`);
      touchMemo();
    } else {
      if (!memo.includes(rovingMessage)) {
        setMemo((prevMemo) => `${rovingMessage} \n\n ${prevMemo}`.trim());
        touchMemo();
      }
    }
  }

  function onEditCancel() {
    onModalDismiss?.();
  }

  /**
   * Triggered when changing the Start or End date of the Request.
   * This is not triggered when toggling a day, or changing a day's start or
   * end time.
   */
  function handleDateChange(params: { startDate?: DateTime; endDate?: DateTime }) {
    let { startDate, endDate } = params;

    // If the start date has changed and is after the end date
    if (startDate && startDate > getIntervalEndDate(intervals)) {
      // Then update the end date to be the start date
      endDate = startDate.endOf("day");
    }
    // If end date has changed and is before the start date
    else if (endDate && endDate < getIntervalStartDate(intervals)) {
      // Then update the start date to be the end date
      startDate = endDate.startOf("day");
    }

    // Generate and set the new intervals given the new start and end date time
    setIntervals((oldSelectableDays) =>
      generateSelectableDaysFromExistingSelectableDays({
        now: NOW,
        startDate: startDate ?? getIntervalStartDate(intervals),
        endDate: endDate ?? getIntervalEndDate(intervals),
        selectableDays: oldSelectableDays,
        schoolHours: school.hours,
        schoolClosures: school.closures,
        shouldUsePreviousWeekdayTimes,
      }),
    );
  }

  /**
   * Triggered when a day is toggled or the start/end time has changed.
   * This is not triggered when changing the start and end date.
   */
  function handleDateTimeChange(newTimesTables: Array<TimesTableRowType>) {
    setIntervals((oldIntervals) =>
      fixStartAndEndTimes({
        oldIntervals,
        newIntervals: newTimesTables,
        now: NOW,
        fixNow: shouldFixNow({ now: NOW, request: request }),
      }),
    );
  }

  /**
   * Triggered when clicking the "Add another day" button and adds a new
   * new interval to the Request. The next interval will be a valid weekday
   * which currently means not a weekend.
   */
  function handleAddAnotherDay() {
    const currentStartDate = intervals[0].startTime.startOf("day");
    const currentEndDate = intervals[intervals.length - 1].endTime.endOf("day");

    const newEndDate = getNextValidDate({
      date: currentEndDate,
      schoolClosures: school.closures,
      subSchedule,
    });

    // Generate and set the new intervals given the new start and end date time
    setIntervals((oldSelectableDays) =>
      generateSelectableDaysFromExistingSelectableDays({
        now: NOW,
        startDate: currentStartDate,
        endDate: newEndDate,
        selectableDays: oldSelectableDays,
        schoolHours: school.hours,
        schoolClosures: school.closures,
        shouldUsePreviousWeekdayTimes,
      }),
    );
  }

  /** Handle changes to the "Additional Edits" field. */
  function handleAdditionalEditsChange(additionalEdits: string) {
    setMultiDayAdjustmentNotes(additionalEdits);
  }

  /*** Render #1 ***/
  /**
   * Duplicate/Clone: Show loading spinner while waiting for availableSubs.
   *
   * When duplicating a request that had priority access subs, we need to wait
   * for the availableSubs query to complete before rendering the form, so we
   * can pre-populate the priority access section correctly.
   *
   * Note: Duplicates use mode="create" with a request prop (not a separate
   * "duplicate" mode).
   */
  if (request && request.preferredSubs?.length && !availableSubs && mode === "create") {
    return <Loader />;
  }

  /*** Constants #2 ***/
  const isRequestWithinCancelWindow =
    request &&
    isDateWithin(
      request.startTime.minus({ hours: request.school.cancelPolicy }),
      request.startTime,
      NOW,
    );

  const showCancelWindowAlert = isEditing && !request?.isFilled() && isRequestWithinCancelWindow;

  const hideCredentialToggle = isEditing
    ? school?.hideCredentialToggle || !request?.isCredentialRequired
    : school?.hideCredentialToggle;
  const showWaitlistAutoFill =
    flags?.waitlistAutoFill && intervals.length > MAX_INTERVALS && !request?.isFilled();

  /*** Render #2 ***/
  return (
    <div
      className="page"
      css={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxWidth: 900,
        margin: "0 auto",
      }}
    >
      {mode === "edit" && (
        <SharedModalHeader
          title={translate("EDIT_REQUEST_HEADER")}
          onDismiss={() => {
            onModalDismiss?.();
          }}
        />
      )}
      {/* Scrollable form area without buttons */}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: 24,
          flex: "1 1 auto",
          overflow: "auto",
          padding: mode === "edit" ? 16 : "16px 0 0 0",
        }}
      >
        {isFilledOrWaitlisted && (
          <Alert
            css={{ marginTop: "var(--ion-safe-area-top)" }}
            color="disclaimer"
            title={translate("EDIT_REQUEST_FILLED_HEADER")}
          >
            {translate("EDIT_REQUEST_FILLED_MESSAGE")}
          </Alert>
        )}

        {/* FIXME: Determine if we want to keep this as a pending action loading
      patter moving forward. */}
        <IonLoading isOpen={isSubmitPending || isEditPending} message="Submitting Request..." />

        {/* Step #0: AMS only */}
        {request?.frontlineAbsenceID && (
          <Card>Currently creating frontline absence with ID {request.frontlineAbsenceID}</Card>
        )}

        {/* Step #1: Select Schools */}
        <Card>
          <SchoolField
            value={schoolId}
            onChange={(schoolId) => handleSchoolChange({ newSchoolId: schoolId })}
            schools={schools}
            isDisabled={
              !userInfo.isSuperAdmin && (isEditing || request?.isFilled() || hasSubInPool)
            }
          />
        </Card>
        {/* Step #2: Select Dates & Times */}
        <Card>
          <RequestFormDateField
            now={NOW}
            school={school}
            intervals={intervals}
            setIntervals={setIntervals}
            // MultiDateField
            onDateChange={handleDateChange}
            // DayField
            onAddAnotherDay={handleAddAnotherDay}
            // MultiDateTimeField - MultiDay
            onDateTimeChange={handleDateTimeChange}
            // MultiDateTimeField - Long Term
            multiDayAdjustmentNotes={multiDayAdjustmentNotes}
            onMultiDayAdjustmentNotesChange={handleAdditionalEditsChange}
            // MultiDateField and DayField
            isWaitlistedOrFilledReq={request?.isFilled() || hasSubInPool}
            originalEndDate={request?.endDate}
            subSchedule={subSchedule}
          />
        </Card>
        {/* Step #3 */}
        <Card>
          <div
            className={styles.withDivider}
            css={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            {/* Step #3.1: Positions */}
            {school?.positions && (
              <PositionField
                positions={school.positions}
                selectedPosition={position}
                onChange={setPosition}
                isDisabled={request?.isFilled() || hasSubInPool}
              />
            )}

            {/* Step #3.2: Teacher */}
            {!isClassroomUser && (
              <TeacherField
                value={teacher}
                teachers={(school as SchoolModel).teachers}
                teacherKind={teacherKind as TeacherKind}
                setTeacherKind={setTeacherKind as () => void}
                setTeacherId={setTeacherId}
                onChange={(teacherSelection) => handleTeacherChange(teacherSelection)}
                isDisabled={!userInfo.isSuperAdmin && (request?.isFilled() || hasSubInPool)}
              />
            )}

            {/* Step #3.3: Grade */}
            <GradeField
              key={schoolId} // Force re-mount when school changes
              initialGradeSelected={grade}
              onChange={(val) => setGrade(val)}
              isDisabled={!userInfo.isSuperAdmin && (request?.isFilled() || hasSubInPool)}
            />

            {/* Step #3.4: Subject */}
            <SubjectField
              subjectValues={subject}
              onChange={setSubject}
              isDisabled={!userInfo.isSuperAdmin && (request?.isFilled() || hasSubInPool)}
            />

            {/* Step #3.6: Contact Number - Show by default for non-super admins, or when school has no admins */}
            {schoolAdmins.length == 0 ? (
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <IonLabel className="font-medium">{translate("LABEL_CONTACT_NUMBER")}</IonLabel>
                <PhoneInput value={contactPhone} onIonInput={setContactPhone} />
              </div>
            ) : (
              <DayOfContactField
                onChange={setDayOfContactAdminId}
                admins={admins}
                defaultAdmin={
                  isEditing
                    ? // Edit mode: Find existing admin or default to empty
                      admins.find((admin) => admin.id === dayOfContactAdminId) || {
                        id: "",
                        name: "",
                      }
                    : // Create mode: Default based on classroom user status
                      isClassroomUser
                      ? { id: "", name: "" }
                      : requestorAdmin
                }
                isRequired={true}
              />
            )}

            {/* Step #3.6: Internal Code */}
            <InternalCodeField
              internalCode={internalCode}
              setInternalCode={setInternalCode}
              formSubmissionErrObj={formSubmissionErrObj}
              required={school.isInternalCodeRequired}
              onFormSubmissionError={(errorObj) => {
                setFormSubmissionErrObj(errorObj);
              }}
              onRequiredFieldTouched={(touchedObj) => {
                setRequiredFieldTouchedObj(touchedObj);
              }}
            />
            {/* Step #3.7: Budget */}
            {hasBudgetsToShow && (
              <BudgetField
                budgets={budgets}
                selectedBudgetId={budgetId}
                onConfirm={setBudgetId}
                formSubmissionErrObj={formSubmissionErrObj}
                onFormSubmissionError={setFormSubmissionErrObj}
                isDisabled={!userInfo.isSuperAdmin && (request?.isFilled() || hasSubInPool)}
              />
            )}

            {/* Step #3.8: Credential Required */}
            {/* Question: Credential / Perming Required? */}
            {!hideCredentialToggle && (
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: request?.isFilled() || hasSubInPool ? Colors.slate300 : undefined,
                }}
              >
                <IonLabel className="font-medium">
                  {translate("REQUEST_FORM_QUESTION_CREDENTIAL_LABEL")}
                </IonLabel>
                <IonToggle
                  aria-label="Credential/Permit requirement toggle"
                  checked={isCredentialRequired}
                  disabled={request?.isFilled() || hasSubInPool}
                  onIonChange={(e: CustomEvent) => {
                    setIsCredentialRequired(e.detail.checked);
                  }}
                  slot="end"
                />
              </div>
            )}

            {/* Step #3.9: Lesson Plan */}
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="flex items-center">
                <IonLabel className="font-medium">
                  {translate("REQUEST_FORM_QUESTION_LESSON_PLAN_LABEL")}
                </IonLabel>
                <button id="internal-code-tooltip" className={styles.unstyledButton}>
                  <IoHelpCircle className={`icon-lg ${styles.helpIcon}`} />
                </button>
                <Tooltip
                  trigger="internal-code-tooltip"
                  header="Lesson Plan Provided?"
                  content="Will your Swing Sub be provided with lesson plans upon arrival? Providing lesson plans will help your Swing Sub be better prepared."
                />
              </div>
              <IonToggle
                aria-label="Lesson Plan Provided toggle"
                slot="end"
                checked={isLessonPlanProvided}
                onIonChange={(e: CustomEvent) => setLessonPlan(e.detail.checked)}
              />
            </div>

            {/* Step #3.8.5: Bilingual Requirement */}
            {isLanguageRequirementEnabled && (
              <LanguageField
                languageValues={bilingualRequirement}
                onChange={setBilingualRequirement}
                isDisabled={!userInfo.isSuperAdmin && (request?.isFilled() || hasSubInPool)}
              />
            )}

            {/* Step #3.9: Notes to Sub */}
            {/* Show an alert when selecting "Special Education" */}
            {subject.includes("SPED") && (
              <Alert showIcon color="info" title={translate("NOTES_TO_SUB_SPED_ALERT_TITLE")}>
                <p
                  css={{ margin: 0 }}
                  dangerouslySetInnerHTML={{ __html: translate("NOTES_TO_SUB_SPED_ALERT_LIST") }}
                />
              </Alert>
            )}

            <NotesToSubField
              memo={memo}
              onChange={setMemo}
              formSubmissionErrObj={formSubmissionErrObj}
              onFormSubmissionError={(errorObj) => {
                setFormSubmissionErrObj(errorObj);
              }}
              onRequiredFieldTouched={(touchedObj) => {
                setRequiredFieldTouchedObj(touchedObj);
              }}
            />

            {/* Step #3.10: Additional Edits */}
            {!!showAdditionalEditField && (
              <AdditionalEditField
                hasAdditionalEdits={hasAdditionalEdits}
                additionalEditMemo={additionalEditMemo}
                setHasAdditionalEdits={(e: boolean): void => {
                  setHasAdditionalEdits(e);
                }}
                setAdditionalEditMemo={(e: string): void => {
                  setAdditionalEditMemo(e);
                }}
              />
            )}
            {!!showWaitlistAutoFill && (
              <WaitlistAutoFillField value={waitlistAutoFill} onChange={setWaitlistAutoFill} />
            )}
          </div>
        </Card>

        {/* Step #4: Priority Access */}
        {mode === "create" && (
          <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Title*/}
            <div css={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h3 css={{ margin: 0, lineHeight: "16px" }}>{translate("SUB_SELECT_TITLE")}</h3>
              <button id="priority-access" className={styles.unstyledButton}>
                <IoHelpCircle className={`icon-lg ${styles.helpIcon}`} />
              </button>
              <Tooltip
                trigger="priority-access"
                header="How priority access works"
                positionAlign="page-center"
                content={<PriorityAccessTooltipContent />}
              />
            </div>
            <Card>
              <div
                className={styles.withDivider}
                css={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <ChooseSubField
                  request={subSelectRequest}
                  hideCredentialToggle={hideCredentialToggle}
                  selectedSubs={priorityAccessSubs}
                  isCredentialRequired={isCredentialRequired}
                  isCredentialedPositionSelected={position?.credsRequired}
                  onConfirm={({ selectedSubs, isCredentialRequired }) => {
                    setPriorityAccessSubs(selectedSubs);
                    setIsCredentialRequired(isCredentialRequired);
                  }}
                />

                {/* Only show Time To Accept when there is at least one sub selected */}
                {priorityAccessSubs.length > 0 && (
                  <TimeToAccept
                    value={timeToAccept}
                    choices={timeToAcceptChoices}
                    onChange={setTimeToAccept}
                  />
                )}
              </div>
            </Card>
          </div>
        )}
        {showCancelWindowAlert && (
          <Alert color="success" css={{ margin: "20px 0" }}>
            <div className="flex w-full">
              <div>{translate("CANCELLATION_WINDOW_LABEL")}</div>
              <button id="present-cancellation-alert" className={styles.unstyledButton}>
                <IoHelpCircle className={`icon-lg ${styles.helpIcon}`} />
              </button>
              <Tooltip
                trigger="present-cancellation-alert"
                content={translate("CANCELLATION_WINDOW_TOOLTIP_MESSAGE")}
              />
            </div>
          </Alert>
        )}
      </div>

      {/* Step #5: Submit */}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          justifyContent: "center",
          width: "100%",
          zIndex: 5,
          padding: 20,
          background: "var(--swing-white300)",
        }}
      >
        {isEditing === false && (
          <Button
            type="primary"
            onClick={() => onCreateFormProgress(intervals)}
            status={!isFormValid() || isEditPending || isSubmitPending ? "disabled" : "enabled"}
            isFluid
          >
            {translate(shouldShowReviewModal ? "REQUEST_REVIEW_TITLE" : "LABEL_REQUEST_SUB")}
          </Button>
        )}
        {isEditing === true && (
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <Button
              type="primary"
              // If the admin is extending a filled/waitlisted request AND adding an additional edit
              // then we want to send the original intervals (start and end date) and let CX change the
              // dates manually
              onClick={() => onSubmit(customizedAdditionalEditMemo ? initialIntervals : intervals)}
              status={!isFormValid() || !isFormChanged() ? "disabled" : "enabled"}
              isFluid
            >
              {translate("EDIT_REQUEST_SUBMIT_LABEL")}
            </Button>
            <Button type="secondary" onClick={onEditCancel} isFluid>
              {translate("EDIT_REQUEST_ACCEPTED_CANCEL_LABEL")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/********** Helper Components **********/
function PriorityAccessTooltipContent() {
  const items: Array<{ icon: IconName; title: string; description: string }> = [
    {
      icon: "Groups",
      title: "Select specific subs",
      description: "Choose specific subs from your favorites and more.",
    },
    {
      icon: "Eye On",
      title: "Control who can see and sign up first",
      description: "Selected subs will be able to see and sign up for your request first.",
    },
    {
      icon: "Clock",
      title: "Decide when to open to all eligible subs",
      description:
        "Choose how long selected subs have to sign up before opening to all eligible subs.",
    },
  ];

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {items.map(({ icon, title, description }, idx) => (
        <div key={`${icon}-${idx}`} css={{ display: "flex", gap: 16 }}>
          <Icon name={icon} />
          {/* Slight padding adjustment to align text w/ icon */}
          <div css={{ paddingTop: 2 }}>
            <p
              css={{
                margin: "0 0 4px",
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              {title}
            </p>
            <p css={{ margin: 0 }}>{description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/***** Helper Functions *****/
/**
 * Returns the toISODate equivalent using the startOf day.
 */
function getIntervalStartDate(intervals: Array<TimesTableRowType>) {
  return intervals
    .filter((interval) => interval.isEnabled)
    .at(0)!
    .startTime.startOf("day");
}

/**
 * Returns the toISODate equivalent using the endOf day.
 */
function getIntervalEndDate(intervals: Array<TimesTableRowType>) {
  return intervals
    .filter((interval) => interval.isEnabled)
    .at(-1)!
    .endTime.endOf("day");
}

type ExtendedRequestEditsParams = {
  initialIntervals: TimesTableRowType[];
  intervals: TimesTableRowType[];
  subSchedule?: SubScheduleDay[];
  additionalEditMemo: string;
};

/**
 * For the "Admin Extend Filled/Waitlisted Requests" feature
 * https://linear.app/swing-education/project/swing-schools-extend-filled-waitlisted-requests-24fa9b608736/overview#heading-additional-edits-f3916d2a
 *
 * @returns @type string The extended request edits.
 */
export function getExtendedRequestEdits(params: ExtendedRequestEditsParams) {
  const { additionalEditMemo, subSchedule = [], intervals, initialIntervals } = params;

  // https://linear.app/swing-education/issue/MKTP-1692/exclude-unchecked-non-school-days-from-support-memo
  // filter out non-school and disabled days
  const enabledDays = intervals.filter((interval) => interval.isEnabled);
  // And filter out conflicting days
  const nonConflictingDays = enabledDays.filter(
    (day) => !subSchedule.some((sub) => isSameDate(day.startTime, sub.start)),
  );
  // Get all of the new dates
  const newDates =
    nonConflictingDays?.filter((interval) => !initialIntervals.includes(interval)) || [];

  if (additionalEditMemo.length === 0 || newDates.length === 0) {
    return undefined;
  }

  const newDatesString = newDates.map((interval) => interval.dateString);

  const extraEditsString = `
    Admin wants to add the following dates/times:
    ${newDatesString.map(
      (date) => `
      - ${formatDate({ date, format: "Wed, Jan 1, 2025" })}`,
    )}

    This is what they wrote: ${additionalEditMemo}
  `;

  return extraEditsString;
}

/** Builds the preferred subs payload for request submission. */
function buildPriorityAccessSubsPayload(params: {
  priorityAccessSubs: Array<SubModel>;
  timeToAccept: number | string | undefined;
}) {
  const { priorityAccessSubs, timeToAccept } = params;

  // Only include when there are selected subs
  if (priorityAccessSubs.length === 0) return {};

  return {
    "preferred-subs": priorityAccessSubs.map((sub) => sub.id),
    ...(timeToAccept === translate("TIME_TO_ACCEPT_ONLY_SELECTED_SUBS_VALUE")
      ? { "only-preferred-can-access": true }
      : {}),
    // Do NOT send general-pool-delay property if priority access is disabled
    ...(typeof timeToAccept === "number" ? { "general-pool-delay": timeToAccept } : {}),
  };
}
